import { IndexedStorage } from './indexed-storage';

export interface IndexProxyUpdateAction<T> {
    keys: Array<keyof T & string>;
    update(record: T): T;
}

export interface IndexProxyRemoveManyPredicate {
    isMatch(id: string, index: string | null | undefined): boolean;
}

/**
 * Wrapper around {@link IndexedStorage} to abstract out callbacks in the public API.
 */
export class QueryableStorageIndexProxy<T extends { id: string }> {
    constructor(
        private readonly index: IndexedStorage<T>,
    ) {}

    add(
        record: T,
        partition?: string,
    ): void | Promise<void> {
        return this.index.add(record, partition);
    }

    async addMany(
        records: T[],
        partition?: string,
    ): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await Promise.all(records.map(r => this.index.add(r, partition)));
    }

    get(
        id: string,
    ): Promise<T | null | undefined> {
        return this.index.get(id);
    }

    update(
        id: string,
        action: IndexProxyUpdateAction<T>,
    ): void | Promise<void> {
        return this.index.update(id, val => action.update(val), () => action.keys);
    }

    remove(
        id: string,
        partition?: string,
    ): void | Promise<void> {
        return this.index.remove(id, partition);
    }

    removeMany(
        predicate: IndexProxyRemoveManyPredicate,
    ): Promise<Set<string>> {
        return this.index.removeMany((id, index) => predicate.isMatch(id, index));
    }

    /**
     * Writes pending changes out now rather than on the next lazy flush, for a record that has to
     * survive the process dying before then.
     */
    flush(): Promise<void> {
        return this.index.flush();
    }
}
